import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { ApiError } from '@/lib/api';
import { useCreateCompany } from '@/lib/companies';
import { setActiveCompanyId } from '@/lib/active-company';

interface CreateCompanyValues {
  name: string;
}

/**
 * Shown when the signed-in user belongs to no company yet. Creating one makes
 * them its admin and seeds an assessment, then becomes the active company.
 */
export function CreateCompanyScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const create = useCreateCompany();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CreateCompanyValues>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const onSubmit = (values: CreateCompanyValues) => {
    create.mutate(
      { name: values.name.trim() },
      {
        onSuccess: (company) => {
          setActiveCompanyId(company.id);
          // Refetch everything under the new active company.
          qc.invalidateQueries();
          navigate('/dashboard', { replace: true });
        },
      },
    );
  };

  return (
    <div className="mx-auto flex max-w-content-narrow flex-col px-5 py-16 md:px-10">
      <div className="rounded-lg border border-border bg-surface p-8 shadow-card">
        <h1 className="font-display text-h2 font-bold text-text-primary">
          Create your company
        </h1>
        <p className="mt-2 text-body text-text-secondary">
          You're not part of a company yet. Create one to start your compliance
          assessment, invite teammates, and generate reports. You can add address
          and profile details afterward in Settings.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-gap-element">
          <Field id="company-name" label="Company name" error={errors.name?.message}>
            <Input
              id="company-name"
              autoFocus
              placeholder="Acme Manufacturing, Inc."
              {...register('name', {
                required: 'Enter a company name',
                validate: (v) => v.trim().length > 0 || 'Enter a company name',
              })}
            />
          </Field>

          {create.error ? (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
              {create.error instanceof ApiError
                ? create.error.message
                : 'Could not create the company. Please try again.'}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={create.isPending || !isValid}>
              {create.isPending ? 'Creating…' : 'Create company'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
